import { Anchor, Button, PasswordInput, Stack, Text } from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthShell } from "../components/AuthShell";
import {
  PASSWORD_RESET_COPY,
  REGISTRATION_PASSWORD_COPY,
  validatePasswordConfirmation,
  validateResetPassword,
} from "../constants/account";
import { AUTH_PATHS } from "../constants/publicUrls";
import { authApi } from "../lib/api";
import { ApiError } from "../lib/apiClient";
import { axioTokens } from "../theme";

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token")?.trim() ?? "";
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const form = useForm({
    initialValues: { password: "", confirm_password: "" },
    validate: {
      password: validateResetPassword,
      confirm_password: (value, values) => validatePasswordConfirmation(values.password, value),
    },
  });

  async function handleSubmit(values: typeof form.values) {
    setSubmitting(true);
    try {
      await authApi.resetPassword(token, values.password);
      notifications.show({
        color: "green",
        title: PASSWORD_RESET_COPY.resetSuccessTitle,
        message: PASSWORD_RESET_COPY.resetSuccessBody,
      });
      navigate(AUTH_PATHS.login);
    } catch (err) {
      notifications.show({
        color: "red",
        title: PASSWORD_RESET_COPY.resetTitle,
        message: err instanceof ApiError ? err.message : "Could not update the password",
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthShell title={PASSWORD_RESET_COPY.resetTitle} subtitle={PASSWORD_RESET_COPY.resetSubtitle}>
      {!token ? (
        <Stack gap={axioTokens.unit[32]}>
          <Text>{PASSWORD_RESET_COPY.resetMissingToken}</Text>
          <Anchor component={Link} to={AUTH_PATHS.forgotPassword}>
            {PASSWORD_RESET_COPY.forgotTitle}
          </Anchor>
        </Stack>
      ) : (
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md">
            <PasswordInput
              label="New password"
              required
              {...form.getInputProps("password")}
              styles={{ visibilityToggle: { display: "none" } }}
            />
            <PasswordInput
              label={REGISTRATION_PASSWORD_COPY.confirmPasswordLabel}
              required
              {...form.getInputProps("confirm_password")}
            />
            <Button type="submit" loading={submitting}>
              {PASSWORD_RESET_COPY.resetCta}
            </Button>
            <Anchor component={Link} to={AUTH_PATHS.login}>
              {PASSWORD_RESET_COPY.backToLogin}
            </Anchor>
          </Stack>
        </form>
      )}
    </AuthShell>
  );
}
